import { isANumber } from '../number';

export function isDarkTheme(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const list = document.body?.classList;
    if (!list) return false;
    return list.contains('dark-mode') || list.contains('webda-dark-mode');
  } catch {
    return false;
  }
}

function adjustGeneratedLightnessPercent(lPercent: number): number {
  if (!isDarkTheme()) return lPercent;
  return Math.max(10, Math.min(59, lPercent * 1.36 + 7));
}

export const black: string = 'var(--black)';
export const absoluteBlack: string = 'var(--absolute-black)';
export const green: string = '#44ba8e';
export const lightGreen: string = 'rgba(25,149,173,0.31)';
export const lightGreenSolid: string = 'rgba(161, 220, 198, 1)';
export const red: string = '#EF4D3E';
export const lightRed: string = 'rgba(214,48,62,0.58)';
export const lightRedSolid: string = 'rgb(227,136,143)';
export const blue: string = '#4095d1';
export const lightBlue: string = 'rgba(0,112,224,0.51)';
export const lightBlueSolid: string = 'rgb(125,179,233)';
export const orange: string = 'rgb(241,150,38)';
export const lightOrange: string = 'rgb(248,202,146)';
export const lighterOrange: string = 'rgb(248,215,175)';
export const purple: string = '#65489C';
export const white: string = 'rgb(246,249,255)';
export const exactWhite: string = 'var(--exact-white)';
export const cobalt: string = '#00399D';
export const gray: string = 'var(--gray)';
export const lightGray: string = 'var(--lightGray)';
export const lightGrayBackground: string = 'rgba(240, 242, 245,0.8)';
export const darkGray: string = 'var(--dark-gray)';
export const ocean: string = '#1995ad';
export const selected: string = 'var(--black)';
export const darkGreen: string = '#00897B';
export const yellow: string = 'var(--yellow)';
export const lightYellow: string = 'rgba(255,233,59,0.47)';
export const lightYellowSolid: string = 'rgb(247,237,160)';
export const lighterYellow: string = 'rgba(255,233,59,0.27)';
export const whitesmoke: string = 'var(--white-smoke)';
export const darkWhiteSmoke: string = 'var(--dark-white-smoke)';
export const badgeCardBg: string = 'var(--badge-card-bg)';
export const formColor1: string = 'var(--form-color-1)';
export const primaryFormColor: string = 'var(--primary-color)';

// webda dark mode
export const WEBDA_DARK_BG = 'var(--webda-bg)';
export const NORMAL_CELL_COLOR = 'var(--webda-normal-cell-color)';
export const HOVER_CELL_COLOR = 'var(--webda-hover-cell-color)';
//
export const boxShadow1 = 'var(--box-shadow-1)';
export const btnBoxShadow = 'var(--btn-box-shadow)';

export const border1 = 'var(--border-1)';
export const border2 = 'var(--border-2)';
export const cardBorder = 'var(--border-card)';

/**
 * Make a hexadecimal color lighter or darker
 * @param {string} color - hexadecimal color like #ff00ff
 * @param {number} percent - positive number to lighten and negative number to darken
 */
export const shadeColor = (color: string, percent: number): string => {
  if (!/^#[0-9A-Fa-f]{6}$/.test(color)) {
    return color;
  }
  let effectivePercent = percent;
  if (isDarkTheme()) {
    // Lighten less, darken more so controls stay in a usable range on dark UI
    effectivePercent = -percent;
  }
  let R: number = parseInt(color.substring(1, 3), 16);
  let G: number = parseInt(color.substring(3, 5), 16);
  let B: number = parseInt(color.substring(5, 7), 16);

  R = Math.round((R * (100 + effectivePercent)) / 100);
  G = Math.round((G * (100 + effectivePercent)) / 100);
  B = Math.round((B * (100 + effectivePercent)) / 100);

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  const RR: string =
    R.toString(16).length === 1 ? '0' + R.toString(16) : R.toString(16);
  const GG: string =
    G.toString(16).length === 1 ? '0' + G.toString(16) : G.toString(16);
  const BB: string =
    B.toString(16).length === 1 ? '0' + B.toString(16) : B.toString(16);

  return '#' + RR + GG + BB;
};

export function lightenHexHsl(hex: string, amount: number): string {
  // Normalize input
  let r: number,
    g: number,
    b: number,
    a: number = 255;

  if (/^#([0-9a-f]{3,4})$/i.test(hex)) {
    // #RGB or #RGBA
    const raw = hex.slice(1);
    r = parseInt(raw[0] + raw[0], 16);
    g = parseInt(raw[1] + raw[1], 16);
    b = parseInt(raw[2] + raw[2], 16);
    if (raw.length === 4) a = parseInt(raw[3] + raw[3], 16);
  } else if (/^#([0-9a-f]{6})([0-9a-f]{2})?$/i.test(hex)) {
    // #RRGGBB or #RRGGBBAA
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
    if (hex.length === 9) a = parseInt(hex.slice(7, 9), 16);
  } else {
    return hex;
  }

  // Normalize RGB
  const rn = r / 255,
    gn = g / 255,
    bn = b / 255;

  // RGB → HSL
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;

  let h = 0;
  if (d !== 0) {
    if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h /= 6;
  }

  let l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));

  // Adjust lightness (in dark theme, lighten less / darken more)
  let adjust = amount;
  if (isDarkTheme()) {
    adjust = amount > 0 ? amount * 0.42 : amount * 1.12;
  }
  l = Math.min(1, l + adjust);

  // HSL → RGB
  function hue2rgb(p: number, q: number, t: number): number {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }

  let r2: number, g2: number, b2: number;
  if (s === 0) {
    r2 = g2 = b2 = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r2 = hue2rgb(p, q, h + 1 / 3);
    g2 = hue2rgb(p, q, h);
    b2 = hue2rgb(p, q, h - 1 / 3);
  }

  // Build hex
  const toHex = (n: number) =>
    Math.round(n * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r2)}${toHex(g2)}${toHex(b2)}${a.toString(16).padStart(2, '0')}`;
}

export const hashCode = (str: string) => {
  let hash = 0,
    i,
    chr;
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(str.length - i - 1);
    hash = (hash << 6) - hash + chr * 1000;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

export const stringToColour = (
  group: string,
  a: number = 1,
  light: boolean | null = false,
  str?: string,
  score?: number | undefined | null,
  maxScore?: number | undefined | null,
  ss?: number,
  ll?: number,
  forceRandomColorWithScore: boolean = false
) => {
  if (maxScore && !forceRandomColorWithScore) {
    const scoreL = isDarkTheme() ? 59 : 49;
    const grayL = isDarkTheme() ? 59 : 49;
    if (isANumber(score))
      return `hsla(${(113 * Number(score)) / maxScore}, 83%, ${scoreL}%, 1)`;
    return `hsla(0, 0%, ${grayL}%, 1)`;
  }
  const hash = Math.abs(hashCode(group ?? '') * 113);
  const s = isANumber(ss)
    ? ss
    : !str
      ? 85
      : (Math.abs(hashCode(str)) % 37) * 2 + 38;
  let l = isANumber(ll)
    ? ll
    : light === null
      ? isANumber(score) && isANumber(maxScore)
        ? Math.abs(score! / maxScore!) * 10 * 3 + 15
        : (Math.abs(hashCode(str || '') * hashCode(str || '')) % 23) * 3 + 10
      : light
        ? 75
        : 45;
  l = l ?? 45;
  if (isDarkTheme() && !light) l += 20;
  const h = (Math.abs(hash) % (320 - 160)) + 160;
  const lOut = adjustGeneratedLightnessPercent(l ?? 45);
  return 'hsla(' + h + ',' + s + '%,' + lOut + '%,' + a + ')';
};

export const stringToColourBW = (str: string, a: number = 1) => {
  const hash = hashCode(str);
  const s = 0;
  const l = isDarkTheme() ? 14 + (Math.abs(hash) % 26) : hash / 80;
  const h = 0;
  return 'hsla(' + h + ',' + s + '%,' + l + '%,' + a + ')';
};

export const generatePercentageColor = (
  currPercentage: number,
  prevPercentage?: number | string,
  asClass?: boolean
  // baseLine?: number,
  // target?: number
): string => {
  const baseLinePercentage = 0;
  // if (baseLine && target && baseLine < target) {
  //   baseLinePercentage = (baseLine / target) * 100;
  // }
  const dark = isDarkTheme();
  if (currPercentage >= 100) {
    return asClass
      ? 'traffic_green'
      : dark
        ? 'rgba(16, 110, 0, 0.58)'
        : 'rgba(27, 193, 0, 0.5)'; // green
  } else if (currPercentage <= baseLinePercentage) {
    return asClass
      ? 'traffic_red'
      : dark
        ? 'rgba(180, 36, 36, 0.58)'
        : 'rgba(248, 0, 0, 0.5)'; // red
  } else if (currPercentage > baseLinePercentage && currPercentage < 100) {
    /*if (
      typeof prevPercentage === 'number' &&
      currPercentage >= prevPercentage
    ) {
      return asClass ? 'traffic_light_green' : '#B9D990'; // light green
    } else*/ if (
      (typeof prevPercentage === 'number' && currPercentage < prevPercentage) ||
      typeof prevPercentage === 'undefined'
    ) {
      return asClass
        ? 'traffic_yellow'
        : dark
          ? 'rgba(185, 145, 28, 0.58)'
          : 'rgba(255, 225, 71, 0.5)'; // yellow
    }
  }
  return 'var(--exact-white)';
};
